/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-len */
import { EventsHero } from '../blocks/EventsHero';
import { IconEndlessScroll } from '../blocks/IconEndlessScroll';
import { InputType } from '../../types';
import { CTATileCarousel } from '../blocks/CTATileCarousel';
import LogoCardGrid from '../blocks/LogoCardGrid';
import MeetTheTeam from '../blocks/MeetTheTeam';
import { ModalForm } from '../forms/ModalForm';
import { useState } from 'react';

export const StreamTV2026Page = () => {
  // State for show/hide forms and setting their associated fields
  const [showFormModal, setShowFormModal] = useState(true);
  const [currentFormConfig, setCurrentFormConfig] = useState<(typeof formConfigs)[keyof typeof formConfigs] | null>(null);

  // Form configurations
  const formConfigs = {
    meetWithAdvertising: {
      heading: '<h3>Meet with<br/><strong>Xumo Advertising</strong></h3>',
      successMessage: 'Thank you for contacting us! Our team has received your inquiry and will connect with you after reviewing your submission.',
      source: 'meet-the-team',
      emailSubject: 'StreamTV 2026 - Connect with Xumo Advertising',
      collection: 'streamtv_2026_submissions',
    },
  };

  // Shared form fields for all forms
  const formFields = [
    { type: InputType.Name, name: 'first_name', placeholder: 'First name' },
    { type: InputType.Name, name: 'last_name', placeholder: 'Last name' },
    { type: InputType.Email, name: 'email', placeholder: 'Email' },
    { type: InputType.Name, name: 'company', placeholder: 'Company' },
    { type: InputType.Textarea, name: 'message', placeholder: 'Message', width: 'full' },
  ];

  // Helper to set correct form config
  const openForm = (configKey: keyof typeof formConfigs) => {
    setCurrentFormConfig(formConfigs[configKey]);
    setShowFormModal(true);
  };

  return (
    <>
      <EventsHero
        bgColor="xumoDarkPlum"
        textColor="white"
        eyebrow={'<span>Xumo</span> <span>at</span> <strong>StreamTV</strong> <strong>2026</strong>'}
        title={'<span>Fueling</span> <span>the</span><br/><strong>future</strong> <strong>of</strong> <strong>FAST</strong>'}
        body={[
          {
            headingAlt: 'June 16-19',
            className: 'type-heading-alt weight-500',
          },
          {
            bodyBase: "At StreamTV, Xumo is bringing together the platform, partners, and innovation shaping what's next.",
            className: 'type-subheading-xs mt-8 max-w-[914px]',
          },
        ]}
        imageSrc="/static/images/refresh/2026/streamtv/streamtv-hero.webp"
      />
      <MeetTheTeam
        bgColor={'xumoDarkPlum'}
        cardTextColor={'white'}
        imageTiles={true}
        headerContent={[
          { title: 'Industry-recognized <strong>excellence</strong>', className: 'text-white mb-8 lg:mb-10' },
          {
            bodyBase: 'Xumo recognized in seven StreamTV award categories',
            className: 'text-white mb-8 lg:mb-10',
          },
          {
            ctaRow: [
              {
                label: "Read about Xumo's 2025 wins",
                href: '/press/xumo-wins-big-at-2025-stream-tv-awards',
                color: 'xumoAltBerryLight',
                light: true,
                className: 'mx-auto',
              },
            ],
          },
        ]}
        carouselItems={[
          {
            awardCategory: 'Advertising Executive of the Year',
            imageSrc: '/static/images/refresh/2026/streamtv/headshots/ying-wang.png',
            name: 'Ying Wang',
            title: 'General Manager, Xumo Advertising, Xumo',
          },
          {
            awardCategory: 'Content Partnerships Executive of the Year - Acquisition',
            imageSrc: '/static/images/refresh/2026/streamtv/headshots/anthony-layser.png',
            name: 'Anthony Layser',
            title: 'Executive Director, Content Acquisitions & Programming Strategy, Xumo',
          },
          {
            awardCategory: 'Content Partnerships Executive of the Year - Distribution',
            imageSrc: '/static/images/refresh/2026/streamtv/headshots/stefan-van-engen.png',
            name: 'Stefan Van Engen',
            title: 'VP, Content Partnership, Acquisition, Distribution & Experience, Xumo',
          },
          {
            awardCategory: 'Emerging Leaders',
            imageSrc: '/static/images/refresh/2026/streamtv/headshots/chelsea-duong.png',
            name: 'Chelsea Duong',
            title: 'Senior Manager, Content Planning & Acquisition, Xumo',
          },
          {
            awardCategory: 'Emerging Leaders',
            imageSrc: '/static/images/refresh/2026/streamtv/headshots/lady-learned.png',
            name: 'Lady Learned',
            title: 'Vice President, Business Operations & Planning, Xumo',
          },
          {
            awardCategory: 'Marketing Campaign of the Year',
            imageSrc: '/static/images/refresh/2026/streamtv/headshots/nascar.png',
            name: 'Xumo x NASCAR: Driving Brand Lift Through Content, Culture, and Connection',
          },
          {
            awardCategory: 'Marketing Campaign of the Year',
            imageSrc: '/static/images/refresh/2026/streamtv/headshots/xumo-play.png',
            name: 'Xumo x NASCAR: Driving Brand Lift Through Content, Culture, and Connection',
          },
        ]}
      />
      <LogoCardGrid
        stackedContent={[
          {
            heading: 'Setting the standard for<br/><strong>FAST distribution and monetization</strong>',
            className: 'text-white mb-0 md:mb-8',
          },
          {
            bodyBase:
              'Xumo is shaping the evolution of streaming – bringing together the technology, distribution, and partnerships powering the FAST ecosystem. From device to platform to enterprise solutions, these are the teams – and capabilities – driving what’s next.',
            className: 'text-white text-[18px] md:text-[20px]',
          },
        ]}
        logoCards={[
          {
            image: { src: '/static/images/refresh/2025/ces/XUMO_ENTERPRISE_LOGO-Stacked-FullColorReverse-RGB 2.png', alt: 'Xumo Enterprise' },
            body: "We're leading the future of FAST with flexible solutions and tech stacks built to adapt to you.",
          },
          {
            image: { src: '/static/images/refresh/2025/ces/XUMO_PLAY_LOGO-Stacked-FullColorReverse-RGB-1.png', alt: 'Xumo Play' },
            body: "We're innovating premium, free ad-supported content experiences that revolutionize the way you watch.",
          },
          {
            image: { src: '/static/images/refresh/2025/ces/XUMO_STREAM_BOX_LOGO-Stacked-FullColorRevise-RGB-1.png', alt: 'Xumo Stream Box' },
            body: "We're partnering with operators of all sizes to distribute our platform as a modern streaming solution that gets you.",
          },
          {
            image: { src: '/static/images/refresh/2025/ces/XUMO_TV_LOGO-FullColorReverse-RGB-1.png', alt: 'Xumo TV' },
            body: "We're taking our smart TV platform to the next level with developments made for the way you stream.",
          },
        ]}
      />
      <MeetTheTeam
        bgColor={'xumoDarkPlum'}
        cardTextColor={'white'}
        headerContent={[
          {
            image: {
              src: '/static/images/refresh/2025/ces/1_XUMO_ADVERTISING_LOGO-Horizontal-FullColorReverse-RGB.png',
              alt: 'Xumo Advertising',
              width: 500,
              height: 41,
            },
            className: 'w-[351px] lg:w-[500px] h-auto',
          },
          { title: 'Where streaming gets smarter<br/>and <strong>advertising gets better</strong>', className: 'text-white mt-12 mb-12 lg:mt-16 lg:mb-16' },
          {
            bodyBase:
              'Streaming has never been bigger. It’s also never been more complicated. At StreamTV 2026, Xumo is showing what happens when streaming finally works for advertisers. We’re bringing together scale, simplicity and measurable impact – and we’d love to connect.',
            className: 'text-white mb-12',
          },
        ]}
        cta={[
          {
            ctaRow: [
              {
                label: 'Connect with Xumo at StreamTV',
                color: 'xumoAltBerryLight',
                light: true,
                fullalways: true,
                onClick: () => openForm('meetWithAdvertising'),
              },
            ],
          },
        ]}
      />
      <IconEndlessScroll
        bg="#140D13"
        textbox={false}
        copy={[
          { heading: 'We&#39;re <strong>tuned in</strong>', className: 'text-white' },
          {
            body: 'Xumo partners with brands across the entire streaming industry — from consumer electronics to advertisers — to help get you wherever you want to go.',
            className: 'text-white',
          },
        ]}
        icons={[
          [
            '/static/images/refresh/2025/enterprise-logos/1-1.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-2.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-3.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-4.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-5.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-6.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-7.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-8.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-9.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-10.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-11.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-12.jpg',
            '/static/images/refresh/2026/q1-updates/pbs-logo.png',
            '/static/images/refresh/2025/enterprise-logos/1-14.jpg',
            '/static/images/refresh/2025/enterprise-logos/1-15.jpg',
          ],
          [
            '/static/images/refresh/2025/enterprise-logos/2-1.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-2.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-3.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-4.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-5.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-6.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-7.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-8.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-9.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-10.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-11.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-12.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-13.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-14.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-15.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-16.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-17.jpg',
            '/static/images/refresh/2025/enterprise-logos/2-18.jpg',
          ],
          [
            '/static/images/refresh/2025/enterprise-logos/3-1.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-2.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-3.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-4.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-5.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-6.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-7.jpg',
            '/static/images/refresh/2026/q1-updates/peacock-logo.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-9.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-10.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-11.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-12.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-13.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-14.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-15.jpg',
            '/static/images/refresh/2025/enterprise-logos/3-16.jpg',
          ],
        ]}
      />
      <CTATileCarousel
        bgColor="xumoDarkPlum"
        cardBgColor="xumoDarkPlum"
        center={false}
        bgImage="/static/images/refresh/2025/ces/Xumo_Swirl_Transparent_16k.png"
        header={[{ title: 'Xumo at StreamTV', className: 'text-white mb-12 md:mb-24 text-center mx-auto w-[82%] md:w-full' }]}
        tiles={[
          [
            { headingAlt: "The OS Leaders' Roundtable", className: 'text-white' },
            { subheadingSmall: 'June 17 | 10&#58;20 AM - 11&#58;00 AM', className: 'text-white mt-4' },
            {
              bodyBase: 'The Battleground of Power, Personalization & the New Economics of CTV',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              bodyBase: 'Hong Kwon, Rob Caruso, James Faris (Moderator)',
              className: 'text-white max-w-[700px] mb-4',
            },
          ],
          [
            { headingAlt: 'Keynote Fireside Chat', className: 'text-white' },
            { subheadingSmall: 'June 17 | 5&#58;05 PM - 5&#58;25 PM', className: 'text-white mt-4' },
            {
              bodyBase: 'MVPDs in the Streaming Era&#58; Reinventing the Bundle',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              bodyBase: 'Erik Bortz (Moderator), John Dixon, Elena Ritchie',
              className: 'text-white max-w-[700px] mb-4',
            },
          ],
          [
            { headingAlt: 'Panel', className: 'text-white' },
            { subheadingSmall: 'June 18 | 8&#58;50 AM - 9&#58;25 AM', className: 'text-white mt-4' },
            {
              bodyBase: 'Smarter Programming & Curation Across FAST and Premium Platforms',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              bodyBase: 'Chris Hall, Victoria Marvin, TBD, Moderator (TBD)',
              className: 'text-white max-w-[700px] mb-4',
            },
          ],
          [
            { headingAlt: 'Panel', className: 'text-white' },
            { subheadingSmall: 'June 18 | 9&#58;45 AM - 10&#58;20 AM', className: 'text-white mt-4' },
            {
              bodyBase: 'FAST Meets Creator TV: Building Channels Audiences Actually Watch',
              className: 'text-white max-w-[700px] mb-4',
            },
            {
              bodyBase: 'Anthony Layser, Emily Posey, Jonathan Shrank, Dade Hayes (Moderator)',
              className: 'text-white max-w-[700px] mb-4',
            },
          ],
          [
            { subheadingSmall: 'Reception', className: 'text-white mb-2' },
            { headingAlt: 'After Hours', className: 'text-white' },
            { subheadingSmall: 'June 18 | 9&#58;00 PM - 12&#58;00 AM | Mountain View Pavilion', className: 'text-white mt-4' },
            {
              bodyBase: 'Sponsored by Xumo',
              className: 'text-white max-w-[700px] mb-4',
            },
          ],
        ]}
      />

      {/* Single modal with dynamic content */}
      {currentFormConfig && (
        <ModalForm
          isDark={true}
          isOpen={showFormModal}
          onClose={() => setShowFormModal(false)}
          heading={currentFormConfig.heading}
          formFields={formFields}
          CTA={{ label: 'Submit' }}
          formAction={{
            submissionEndpoint: `/api/contact?email_subject=${encodeURIComponent(currentFormConfig.emailSubject)}&collection=${currentFormConfig.collection}`,
            onSuccess: () => setShowFormModal(false),
            successMessage: currentFormConfig.successMessage,
            formType: 'event',
          }}
          styleOverrides={{
            form: 'mt-16 gap-2',
            formField: 'rounded-none outline-[0.5px] bg-transparent outline-[#FFFFFF] placeholder:text-white',
            hideLabel: true,
            CTA: { alignment: 'w-full md:w-fit self-center mt-6 bg-[#FFFFFF] text-[#000000]' },
            accentColor: 'xumoAltBerryLight',
            hoverColor: 'xumoAltBerryDark',
          }}
        />
      )}
    </>
  );
};
